import _ from 'lodash';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect, useCallback } from 'react';

import { Box } from '@mui/system';
import Card from '@mui/material/Card';
import Stack from '@mui/system/Stack';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import { Button, MenuItem, Skeleton, CardContent } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { TITLE_OPTIONS } from 'src/utils/title-options';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { useUploadFileMutation } from 'src/services/files/file.service';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';
import { useSetCandidateProfileMutation } from 'src/services/candidates/candidates.service';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import { useGetCandidateSummaryFieldsQuery } from 'src/services/settings/candidate-summary-fields.service';
import {
  CandidateProfile,
  CandidateAvailabilityStatus,
} from 'src/services/candidates/candidates.types';
import { isShadowEmail, isAvailabilityFeatureLocked, generateCandidateProfileErrorMessage } from 'src/services/candidates/candidates.utils';

import Iconify from 'src/components/iconify';
import EntityAvatar from 'src/components/entity-avatar';
import LinkedInLink, { linkedinUrlFromLinks } from 'src/components/linkedin-link';
import DateDisplay from 'src/components/date/date-display';
import FormProvider from 'src/components/hook-form/form-provider';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import ImageCropper from 'src/components/image-cropper/image-cropper';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import FeatureLockedChip from 'src/components/billing/feature-locked-chip';
import AvailabilityStatusLabel from 'src/components/candidates/availability-status-label';

import { isSummaryTitleSelected, buildCandidateSummaryMeta } from 'src/sections/candidates/candidate-summary-meta';
import { buildDialNumber, useCommunicationActions } from 'src/sections/admin/integrations/communications/use-communication-actions';

import { TenantType } from 'src/types/enums';


type Props = {
  profile?: CandidateProfile;
  isLoading: boolean;
  showAvailabilityStatus?: boolean;
  mode?: 'edit' | 'view';
  // Live (synced) profile location, shown read-only in the summary line when the CRM location is
  // empty (JT1-1535). The live profile is never written from here.
  fallbackLocation?: string;
  // OrganizationCandidate id — enables click-to-call/SMS from the actions menu
  commsEntityId?: string;
  onUpdate?: () => void;
  onImportProfile?: (profile: CandidateProfile) => void;
  onExportProfile?: (profile: CandidateProfile) => void;
  onAddToJob?: (profile: CandidateProfile) => void;
  onSendForSigning?: () => void;
  onScheduleMeeting?: () => void;
};

export default function ProfileUser({
  profile,
  isLoading,
  showAvailabilityStatus = true,
  mode = "edit",
  fallbackLocation,
  commsEntityId,
  onUpdate,
  onImportProfile,
  onExportProfile,
  onAddToJob,
  onSendForSigning,
  onScheduleMeeting,
}: Props) {
  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const optionsPopover = usePopover();

  const [uploadFile] = useUploadFileMutation();

  const dispatch = useAppDispatch();

  const [photoURL, setPhotoURL] = useState<string>('');

  const [updateProfile] = useSetCandidateProfileMutation();

  const tenantType = useOrgTenant();

  // click-to-call / SMS for the candidate (mirrors the contact profile header);
  // falls back to copy-phone when no comms integration is connected.
  const commsName = `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim();
  const commsNumber = buildDialNumber(profile?.phone_number, profile?.country_code);
  const { commsActive, call, calling, openSms, smsModal } = useCommunicationActions(
    commsNumber,
    commsName,
    commsEntityId ? { entity_type: CommunicationEntityType.CANDIDATE, entity_id: commsEntityId } : undefined,
  );

  const defaultValues = {
    title: '',
    first_name: '',
    last_name: '',
    profile_image_id: '',
    position_title: '',
  };

  const EditUserDetailsSchema = Yup.object().shape({
    title: Yup.string().nullable(),
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    position_title: Yup.string().max(60, t('validation.max_length', { length: 60 })).nullable(),
    profile_image_id: Yup.string().nullable(),
  });

  const methods = useForm({
    resolver: yupResolver(EditUserDetailsSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, isDirty },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateProfile({
        id: profile?.id as string,
        data: {
          title: data.title || undefined,
          first_name: data.first_name as string,
          last_name: data.last_name as string,
          position_title: data.position_title as string,
          profile_image_id: data.profile_image_id ?? undefined,
          sections: [
            ...(profile?.sections?.map((rule) => ({
              type: rule.section_type,
              visibility_rule: rule.visibility_rule,
            })) ?? []),
          ],
        },
      }).unwrap();

      onUpdate?.();
    } catch (e) {
      console.log(e);

      enqueueSnackbar(generateCandidateProfileErrorMessage(e), { variant: 'error' });
    }
  });

  const onEditPhoto = useCallback(
    (imageId: string, publicUrl: string) => {
      setPhotoURL(publicUrl);
      setValue('profile_image_id', imageId);

      onSubmit();
    },
    [onSubmit, setValue]
  );

  // View-mode (recruiter CRM) avatar upload/remove — writes only the photo to the durable attached
  // profile via the same endpoint as the edit form; the live profile is never touched (JT1-1539).
  const handlePhotoChange = useCallback(
    async (fileId: string | null) => {
      if (!profile?.id) return;
      try {
        await updateProfile({ id: profile.id, data: { profile_image_id: fileId } }).unwrap();
        onUpdate?.();
      } catch (e) {
        enqueueSnackbar(generateCandidateProfileErrorMessage(e), { variant: 'error' });
      }
    },
    [updateProfile, profile?.id, onUpdate, enqueueSnackbar]
  );

  // Recruiters manage the CRM copy's photo; candidates edit their own profile via the edit form, and
  // clients get a read-only view.
  const canEditPhoto = tenantType === TenantType.Recruiter;

  useEffect(() => {
    if (!isLoading) {
      setPhotoURL(profile?.profile_image?.public_path ?? '');
      reset(
        {
          title: profile?.title ?? '',
          first_name: profile?.first_name ?? '',
          last_name: profile?.last_name ?? '',
          position_title: profile?.position_title ?? '',
        },
        { keepDirty: false }
      );
    }
  }, [isLoading, profile, reset, setValue]);

  const handleCopyToClipboard = (text: string) => {
    optionsPopover.onClose();

    if (!text) return;

    navigator.clipboard.writeText(text ?? "" as string);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  }

  /**
   * The recipient is the profile's address, but the record is addressed by `commsEntityId` — the
   * OrganizationCandidate id. A CANDIDATE_ENGAGEMENT template resolves against the org-candidate, so
   * passing the CandidateProfile id made getTemplatedContent fail; the mailer swallows that error and
   * the draft silently blanked (JT1-1725). The menu item is hidden without an id to address.
   */
  const handleSendEmail = (row: CandidateProfile) => {
    if (!row.email || !commsEntityId) return;

    dispatch(openSingleMailerDialog({
      emails: [row.email],
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      templateEntityId: commsEntityId,
      targetType: MailerTargetType.CANDIDATE,
      targetId: commsEntityId
    }));

  }

  // Summary line under the name, org-configurable (spec item 17, JT1-1538) from the shared field
  // registry; shared with the record-panel header via one builder. `title`, when selected, drives the
  // subtitle below rather than a meta item; the meta row holds the other selected fields. Location
  // falls back to the live/synced profile when the CRM location is empty (JT1-1535).
  const { data: summaryFieldsData } = useGetCandidateSummaryFieldsQuery();
  const summaryFieldKeys = summaryFieldsData?.fields;
  const summaryMeta = buildCandidateSummaryMeta(profile, t, summaryFieldKeys, fallbackLocation);
  const showTitleSubtitle = isSummaryTitleSelected(summaryFieldKeys);

  if (mode === 'view') {
    return (
      <Card sx={{ margin: 'auto', boxShadow: 'unset' }}>
        <CardHeader sx={{ pb: 0 }} />
        <CardContent>
          <Stack direction="row" alignItems="start" justifyContent="space-between">
            <EntityAvatar
              src={profile?.profile_image?.public_path}
              name={`${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim()}
              size={80}
              editable={canEditPhoto}
              cropTitle={t('components.entity_avatar.candidate_photo_title')}
              onChange={handlePhotoChange}
              sx={{ mr: 3 }}
            />

            <Stack direction="column" alignItems="start" justifyContent="center" flexGrow={1}>
              {isLoading ? (
                <Typography variant="h5">{t('common.loading')}</Typography>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="h5" textTransform="capitalize">
                      {`${profile?.first_name} ${profile?.last_name}`}
                    </Typography>
                    {/* One-click through to LinkedIn (JT1-1480). */}
                    <LinkedInLink url={linkedinUrlFromLinks(profile?.links)} size={18} />
                  </Stack>
                  {showTitleSubtitle && (
                    <Typography variant="caption" gutterBottom>
                      {!_.isEmpty(profile?.position_title)
                        ? _.capitalize(profile?.position_title ?? '')
                        : t('candidates.table.no_position_title')}
                    </Typography>
                  )}

                  {[TenantType.Candidate, TenantType.Recruiter].includes(tenantType) && showAvailabilityStatus && (
                    <>
                      {profile && !isAvailabilityFeatureLocked(profile.availability_status) ? (
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          alignItems="center"
                          justifyContent="start"
                          gap={0.5}
                        >
                          {/* Rendered even when unset: that is "Not shared", not a locked feature
                              (JT1-1620). The lock is the field being absent, not empty. */}
                          <AvailabilityStatusLabel status={profile?.availability_status} selfView={tenantType === TenantType.Candidate} />
                          {profile?.availability_status ===
                            CandidateAvailabilityStatus.AVAILABLE_SOON &&
                            profile?.available_from && (
                              <Stack direction="row" alignItems="center" gap={0.5}>
                                <Typography variant="caption" noWrap>
                                  {t('candidates.table.available_on')}
                                </Typography>
                                <DateDisplay
                                  noWrap
                                  variant="caption"
                                  date={profile?.available_from}
                                  format="Do of MMM YYYY"
                                />
                              </Stack>
                            )}
                        </Stack>
                      ) : (
                        <FeatureLockedChip text={t('candidates.table.availability')} />
                      )}
                    </>
                  )}

                  {summaryMeta.length > 0 && (
                    <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1 }}>
                      {summaryMeta.map((item) => (
                        <Stack key={item.key} spacing={0.25} sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.disabled">
                            {item.label}
                          </Typography>
                          <Box sx={{ typography: 'body2' }}>{item.value}</Box>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                endIcon={<Iconify icon="carbon:overflow-menu-horizontal" />}
                sx={{ mr: 1 }}
                onClick={optionsPopover.onOpen}
              >
                {t('common.actions')}
              </Button>
            </Box>
          </Stack>
        </CardContent>


        <CustomPopover
          open={optionsPopover.open}
          onClose={optionsPopover.onClose}
          arrow="top-right"
        >
          {
            onSendForSigning && (
              <MenuItem
                onClick={() => {
                  optionsPopover.onClose();
                  onSendForSigning();
                }}
              >
                <Iconify icon="mdi:signature-freehand" />
                <Typography variant='body2'>Send for Signing</Typography>
              </MenuItem>
            )
          }

          {
            profile?.email && (
              <MenuItem onClick={() => handleCopyToClipboard(profile?.email as string)}>
                <Iconify icon="mdi:email-outline" />
                <Typography variant='body2'>{t('candidates.detail.actions.copy_email')}</Typography>
              </MenuItem>
            )
          }

          {/* Needs both an address to send to and an org-candidate id to resolve the template
              against — the restricted org-detail view has the first but not the second. */}
          {
            profile?.email && commsEntityId && (
              <MenuItem
                onClick={() => {
                  optionsPopover.onClose();
                  handleSendEmail(profile);
                }}
              >
                <Iconify icon="lsicon:email-send-filled" />
                <Typography variant='body2'>{t('common.send_email')}</Typography>
              </MenuItem>
            )
          }

          {
            onScheduleMeeting && (
              <MenuItem
                disabled={isShadowEmail(profile?.email)}
                onClick={() => {
                  optionsPopover.onClose();
                  onScheduleMeeting();
                }}
              >
                <Iconify icon="mdi:calendar-plus" />
                <Stack>
                  <Typography variant='body2'>{t('calendar.schedule_meeting.action')}</Typography>
                  {isShadowEmail(profile?.email) && (
                    <Typography variant='caption' color='text.secondary'>{t('calendar.schedule_meeting.no_email')}</Typography>
                  )}
                </Stack>
              </MenuItem>
            )
          }

          {
            profile?.phone_number && (
              commsActive ? (
                <>
                  <MenuItem
                    disabled={calling}
                    onClick={() => {
                      optionsPopover.onClose();
                      call();
                    }}
                  >
                    <Iconify icon="solar:phone-bold" />
                    <Typography variant='body2'>{t('integrations.communications.actions.call')}</Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      optionsPopover.onClose();
                      openSms();
                    }}
                  >
                    <Iconify icon="solar:chat-round-dots-bold" />
                    <Typography variant='body2'>{t('integrations.communications.actions.sms')}</Typography>
                  </MenuItem>
                </>
              ) : (
                <MenuItem onClick={() => handleCopyToClipboard(profile?.phone_number as string)}>
                  <Iconify icon="solar:phone-linear" />
                  <Typography variant='body2'>{t('candidates.detail.actions.copy_phone')}</Typography>
                </MenuItem>
              )
            )
          }

          {
            onImportProfile && (
              <MenuItem onClick={() => onImportProfile(profile as CandidateProfile)}>
                <Iconify icon="bx:import" />
                <Typography variant='body2'>{t('candidates.detail.actions.import_profile')}</Typography>
              </MenuItem>
            )
          }

          {
            onExportProfile && (
              <MenuItem onClick={() => onExportProfile(profile as CandidateProfile)}>
                <Iconify icon="bx:export" />
                <Typography variant='body2'>{t('candidates.detail.actions.export_profile')}</Typography>
              </MenuItem>
            )
          }

          {
            onAddToJob && (
              <MenuItem onClick={() => onAddToJob(profile as CandidateProfile)}>
                <Iconify icon="mdi:invite" />
                <Typography variant='body2'>{t('common.add_to_job')}</Typography>
              </MenuItem>
            )
          }
        </CustomPopover>

        {smsModal}
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid xs={12} md={4}>
        <Card sx={{ pt: 4, pb: 4, px: 3, height: '100%', textAlign: 'center' }}>
          <ImageCropper
            onSubmit={onEditPhoto}
            existingUrl={photoURL}
            showGrid
            warningMessage={t('components.image_cropper.warning_message')}
          />
        </Card>
      </Grid>
      <Grid xs={12} md={8} spacing={2}>
        <Card sx={{ height: '100%' }}>
          <CardHeader

            title={
              isLoading ? (
                <Skeleton width={200} />
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  my={1}
                  gap={3}
                  justifyContent="space-between"
                >
                  <Typography variant="h6">{t('account.profile.details')}</Typography>

                  {!isLoading && (
                    <Stack direction="row" alignItems="center" justifyContent="start" gap={1}>
                      {[TenantType.Candidate, TenantType.Recruiter].includes(tenantType) && !isAvailabilityFeatureLocked(profile?.availability_status) && (
                        <Stack direction="column" alignItems="end" justifyContent="end" gap={0.5}>
                          {/* A candidate reading this panel is reading their own record, so an unset
                              availability invites them to set it rather than reporting it (JT1-1620). */}
                          <AvailabilityStatusLabel status={profile?.availability_status} selfView={tenantType === TenantType.Candidate} />
                          {profile?.availability_status ===
                            CandidateAvailabilityStatus.AVAILABLE_SOON &&
                            profile?.available_from && (
                              <Stack direction="row" alignItems="center" gap={0.5}>
                                <Typography variant="caption">
                                  {t('candidates.table.available_on')}
                                </Typography>
                                <DateDisplay
                                  variant="caption"
                                  date={profile?.available_from}
                                  format="DD/MM/YYYY"
                                />
                              </Stack>
                            )}
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Stack>
              )
            }
          />
          <CardContent sx={{ pt: 4 }}>

            {isLoading ? (
              <Skeleton variant="rectangular" height={100} />
            ) : (
              <FormProvider methods={methods} onSubmit={onSubmit}>
                <Grid container spacing={2}>
                  <Grid xs={12}>
                    <RHFSelect
                      name="title"
                      label={t('common.form_labels.title')}
                    >
                      <MenuItem value="">{t('common.none')}</MenuItem>
                      {TITLE_OPTIONS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {t(`enums.title_options.${value}`)}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Grid>

                  <Grid xs={6} sm={6}>
                    <RHFTextField
                      name="first_name"
                      label={t('common.form_labels.first_name')}
                      placeholder={t('common.placeholders.first_name')}
                    />
                  </Grid>

                  <Grid xs={6} sm={6}>
                    <RHFTextField
                      name="last_name"
                      label={t('common.form_labels.last_name')}
                      placeholder={t('common.placeholders.last_name')}
                    />
                  </Grid>

                  <Grid xs={12}>
                    <RHFTextField
                      name="position_title"
                      label={t('common.form_labels.position_title')}
                      placeholder={t('common.placeholders.position_title')}
                    />
                  </Grid>

                  <Grid xs={12} />
                </Grid>

                <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                    disabled={!isDirty}
                  >
                    {t('common.save')}
                  </LoadingButton>
                </Stack>
              </FormProvider>
            )}
          </CardContent>
        </Card>

      </Grid>
    </Grid>
  );
}
